import { useContext, useState } from 'react'
import { ConfigContext } from '../store/ConfigContext'
import { ANALYTICS } from '../store/AnalyticsConstants'
import eventDetailSchema from '../assets/eventDetailSchema.json'
import { Validator } from 'jsonschema'

const validateEvent = (eventDetail) => {
  const validator = new Validator()
  const result = validator.validate(eventDetail, eventDetailSchema)
  if (result.valid) {
    return eventDetail
  }
  throw {
    message: 'Analytic event is not valid!',
    eventDetail,
    errors: JSON.stringify(
      result.errors.map((error) => ({
        property: error.property,
        message: error.message
      }))
    )
  }
}

const buildEventPayload = (action, activity, control, screen) => {
  const eventDetail = {
    action,
    ...(screen.mode ? { screenMode: screen.mode } : {}),
    screenName: screen.name,
    screenPath: screen.path,
    version: ANALYTICS.VERSION,
    ...(activity ? { activity: activity } : {}),
    ...(control.detail ? { controlDetail: control.detail } : {}),
    ...(control.name ? { controlName: control.name } : {}),
    ...(control.actionAuxParams
      ? { actionAuxParams: control.actionAuxParams }
      : {})
  }
  return {
    dateTime: new Date().toISOString(),
    eventDetailType: ANALYTICS.EVENT_DETAIL_TYPE,
    eventCategory: ANALYTICS.EVENT_CATEGORY,
    version: ANALYTICS.ENVELOPE_VERSION,
    eventDetail: validateEvent(eventDetail)
  }
}

function useAnalytics(screen, isModal = false, checkDone = true) {
  const { publishCdmEvent } = useContext(ConfigContext)
  const [done, setDone] = useState(false)

  const { activity, screenPath, screenName } = screen

  const _buildEvent = (
    action,
    { name, detail, mode, actionAuxParams, screenIndex }
  ) => {
    if (mode) {
      const modes = Object.values(ANALYTICS.MODES)
      if (modes.indexOf(mode) === -1) {
        throw new Error('Invalid mode')
      }
    }
    return buildEventPayload(
      action,
      activity,
      {
        name,
        detail,
        actionAuxParams
      },
      {
        name: screenIndex ? screenName.replace('##', screenIndex) : screenName,
        mode,
        path: screenPath
      }
    )
  }

  const _fire = (
    action,
    { name, detail, mode, actionAuxParams, screenIndex },
    metadata = null
  ) => {
    try {
      publishCdmEvent(
        _buildEvent(action, {
          name,
          detail,
          mode,
          actionAuxParams,
          screenIndex
        }),
        metadata
      )
    } catch (error) {
      console.error(error)
    }
  }

  const fireScreenDisplayed = (props = {}, metadata = null) => {
    if (checkDone && done && !props.force) return
    setDone(true)
    _fire(isModal ? 'ModalWindowDisplayed' : 'ScreenDisplayed', props, metadata)
  }

  const fireControlAccordionExpanded = (name, props = {}) => {
    publishCdmEvent(_buildEvent('ControlAccordianExpanded', { name, ...props }))
  }

  const buildButtonClickEvent = (name, props = {}) => {
    if (Object.values(ANALYTICS.BUTTONS).indexOf(name) === -1) {
      throw new Error('Invalid button')
    }
    return _buildEvent('ControlButtonClicked', { name, ...props })
  }

  const fireButtonClick = (name, props = {}) => {
    publishCdmEvent(buildButtonClickEvent(name, props))
  }

  const fireControlDisplayed = (name, props = {}) => {
    _fire('ControlDisplayed', { name, ...props })
  }

  const fireToggleClick = (name, enabled, props = {}) => {
    if (Object.values(ANALYTICS.BUTTONS).indexOf(name) === -1) {
      throw new Error('Invalid toggle')
    }
    _fire(enabled ? 'ControlToggleEnabled' : 'ControlToggleDisabled', {
      name,
      ...props
    })
  }

  const buildHyperLinkClickEvent = (name, props = {}) => {
    return _buildEvent('ControlHyperLinkClicked', { name, ...props })
  }

  const fireHyperLinkClick = (name, props = {}) => {
    publishCdmEvent(buildHyperLinkClickEvent(name, props))
  }

  return {
    buildButtonClickEvent,
    buildHyperLinkClickEvent,
    fireScreenDisplayed,
    fireButtonClick,
    fireToggleClick,
    fireHyperLinkClick,
    fireControlDisplayed,
    fireControlAccordionExpanded
  }
}

export default useAnalytics
